import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import { colors, shadows, getSourceIcon } from "./source.styles";
import NoDataFound from "../../Reports/NoDataFound";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import FacebookIcon from "@mui/icons-material/Facebook";
import GoogleIcon from "@mui/icons-material/Google";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArchiveIcon from "@mui/icons-material/Archive";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import EditSourceSettingsModal from "./EditSourceSettingsModal";
import ArchiveConfirmationModal from "./ArchiveConfirmationModal";

// Capitalize first letter of each word
const capitalizeWords = (str) => {
  if (!str) return str;
  return str
    .split(/[_\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const MiniTrendChart = ({ trend = 0, data = [] }) => {
  const isUp = trend >= 0;
  const color = isUp ? colors.success : colors.danger;

  return (
    <Box
      sx={{
        width: 80,
        height: 32,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {trend !== 0 ? (
        <svg width="80" height="32" viewBox="0 0 80 32">
          <polyline
            points={
              isUp
                ? "0,28 20,20 40,24 60,12 80,8"
                : "0,8 20,12 40,8 60,18 80,24"
            }
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <Box sx={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>
          -
        </Box>
      )}
    </Box>
  );
};

const getSourceIconComponent = (sourceName) => {
  const name = sourceName?.toLowerCase() || "";
  if (name.includes("facebook")) return <FacebookIcon sx={{ fontSize: 16 }} />;
  if (name.includes("google") && name.includes("ads"))
    return <GoogleIcon sx={{ fontSize: 16 }} />;
  if (name.includes("google")) return <GoogleIcon sx={{ fontSize: 16 }} />;
  if (name.includes("instagram"))
    return <InstagramIcon sx={{ fontSize: 16 }} />;
  return <LanguageIcon sx={{ fontSize: 16 }} />;
};

const DetailedSourcePerformance = ({ data = [] }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const open = Boolean(anchorEl);

  // Filter data based on search query
  const filteredData = data.filter((item) =>
    item.source?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  const handleMenuItemClick = (action, rowData) => {
    if (action === "detailed-performance") {
      const sourceName = rowData?.source
        ? encodeURIComponent(rowData.source)
        : "";
      navigate(`/reports/detailed-source-performance/${sourceName}`);
    } else if (action === "edit-source") {
      setSelectedSource(rowData);
      setEditModalOpen(true);
    } else if (action === "archive-source") {
      setSelectedSource(rowData);
      setArchiveModalOpen(true);
    } else {
      console.log(`Menu action: ${action}`, rowData);
    }
    handleMenuClose();
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedSource(null);
  };

  const handleArchiveModalClose = () => {
    setArchiveModalOpen(false);
    setSelectedSource(null);
  };

  const handleArchiveConfirm = () => {
    console.log("Archiving source:", selectedSource);
    handleArchiveModalClose();
  };

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    px: 2,
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: colors.textMuted,
            textTransform: "uppercase",
          }}
        >
          Detailed Source Performance
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search sources..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 6,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.borderLight,
                },
                "&:hover fieldset": {
                  borderColor: colors.border,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 0.75,
              },
            }}
          />
          <IconButton
            sx={{
              color: colors.textMuted,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 1.5,
              "&:hover": {
                bgcolor: colors.bgPrimary,
                borderColor: colors.border,
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Source</TableCell>
            <TableCell sx={headerCellSx} align="center">
              Total Leads
            </TableCell>
            <TableCell sx={headerCellSx} align="center">
              Conversion Rate
            </TableCell>
            <TableCell sx={headerCellSx} align="center">
              Revenue
            </TableCell>
            <TableCell sx={headerCellSx} align="center">
              Trend (7D)
            </TableCell>
            <TableCell sx={headerCellSx} align="center">
              Quality
            </TableCell>
            <TableCell sx={headerCellSx} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ border: "none" }}>
                <NoDataFound />
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((row, index) => {
              const iconStyle = getSourceIcon(row.source);
              const isPositive = row.trend7Days >= 0;
              const qualityColor =
                row.qualityLabel === "Excellent"
                  ? colors.success
                  : row.qualityLabel === "Good"
                    ? colors.chartBlue
                    : row.qualityLabel === "Average"
                      ? colors.chartOrange
                      : colors.danger;

              return (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      bgcolor: colors.bgPrimary,
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {/* Source */}
                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: iconStyle.bg,
                          color: iconStyle.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getSourceIconComponent(row.source)}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.textPrimary,
                        }}
                      >
                        {capitalizeWords(row.source)}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Total Leads */}
                  <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: colors.textPrimary,
                      }}
                    >
                      {row.totalLeads}
                    </Typography>
                  </TableCell>

                  {/* Conversion Rate */}
                  <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color:
                            row.conversionRate > 0
                              ? colors.success
                              : colors.textSecondary,
                        }}
                      >
                        {row.conversionRate}%
                      </Typography>
                      {row.trend7Days !== 0 && (
                        <Chip
                          label={`${row.trend7Days > 0 ? "+" : ""}${row.trend7Days}%`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: isPositive ? "#ECFDF5" : "#FEF2F2",
                            color: isPositive ? colors.success : colors.danger,
                            "& .MuiChip-label": {
                              px: 0.75,
                              py: 0.25,
                            },
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>

                  {/* Revenue */}
                  <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color:
                          row.revenue > 0
                            ? colors.success
                            : colors.textSecondary,
                      }}
                    >
                      {row.revenue > 0
                        ? `₹${row.revenue.toLocaleString()}`
                        : "-"}
                    </Typography>
                  </TableCell>

                  {/* Trend (7D) */}
                  <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <MiniTrendChart trend={row.trend7Days || 0} />
                  </TableCell>

                  {/* Quality */}
                  <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: qualityColor,
                        }}
                      >
                        {row.qualityScore}
                      </Typography>
                      <Chip
                        label={capitalizeWords(row.qualityLabel)}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: qualityColor + "20",
                          color: qualityColor,
                          "& .MuiChip-label": {
                            px: 0.75,
                            py: 0.25,
                            textTransform: "capitalize",
                          },
                        }}
                      />
                    </Stack>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, index)}
                      sx={{
                        color: colors.textMuted,
                        "&:hover": {
                          bgcolor: colors.bgPrimary,
                        },
                      }}
                    >
                      <MoreHorizIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            minWidth: 200,
          },
        }}
      >
        <MenuItem
          onClick={() =>
            handleMenuItemClick(
              "detailed-performance",
              selectedRowIndex !== null ? filteredData[selectedRowIndex] : null,
            )
          }
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 13,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <ListItemIcon>
            <AssessmentIcon sx={{ fontSize: 18, color: colors.textMuted }} />
          </ListItemIcon>
          <ListItemText
            primary="Detailed Performance"
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleMenuItemClick(
              "export-raw",
              selectedRowIndex !== null ? filteredData[selectedRowIndex] : null,
            )
          }
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 13,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <ListItemIcon>
            <FileDownloadIcon sx={{ fontSize: 18, color: colors.textMuted }} />
          </ListItemIcon>
          <ListItemText
            primary="Export Data"
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>

      {/* Edit Source Settings Modal */}
      <EditSourceSettingsModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        sourceData={selectedSource}
      />

      {/* Archive Confirmation Modal */}
      <ArchiveConfirmationModal
        open={archiveModalOpen}
        onClose={handleArchiveModalClose}
        onConfirm={handleArchiveConfirm}
        sourceName={selectedSource?.source || ""}
      />
    </Box>
  );
};

export default DetailedSourcePerformance;
